/* Selectorul de culoare „Preview stabil" — folosit de varianta alternativa de Home
   (`sections/td-home-palette-categories.liquid`).

   Diferenta fata de harta din /pages/paleta: banda NU se deplaseaza niciodata. Nuanta de
   sub cursor se latteste, vecinele se maresc progresiv si discret, iar restul isi cedeaza
   spatiul — latimea totala a benzii ramane aceeasi, deci nimic nu „sare" sub mouse.

   Datele vin din acelasi `[data-palette-data]` ca pagina Paleta (snippetul `palette-data`),
   iar hex-urile si ordinea pe spectru din `window.TD_PALETTE_UTILS` (definit in
   palette-explorer.js). Nu exista nicio a doua baza de date de culori.

   Se incarca doar pe pagina care foloseste sectiunea, nu global. */
(() => {
  const U = () => window.TD_PALETTE_UTILS || null;

  /* Cat de mult se lateste nuanta de sub cursor si vecinele ei, dupa distanta.
     Peste distanta 3 raman la 1 — de-aia efectul se citeste ca o lupa discreta,
     nu ca o deformare a intregii benzi. */
  const WEIGHTS = [3.2, 2, 1.5, 1.2];

  /* In aceste conditii banda e o grila (vezi `td-home-palette.css`), unde `flex-grow` n-are
     niciun efect. Fara verificarea asta, fiecare miscare de deget ar scrie degeaba 156 de
     stiluri. Conditia e aceeasi in ambele fisiere — daca se schimba, se schimba in amandoua. */
  const GRID_MODE = window.matchMedia('(max-width: 749px), (hover: none)');

  function money(cents, format) {
    const f = format || '{{amount}}';
    const withComma = /amount_with_comma_separator|amount_no_decimals_with_comma_separator/.test(f);
    const noDecimals = /no_decimals/.test(f);
    const value = cents / 100;
    let out = noDecimals ? String(Math.round(value)) : value.toFixed(2);
    /* Separatorul de mii urmeaza formatul magazinului: „1.234,56" cand Shopify cere
       varianta cu virgula, „1,234.56" altfel. */
    const parts = out.split('.');
    const groups = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, withComma ? '.' : ',');
    out = parts[1] ? groups + (withComma ? ',' : '.') + parts[1] : groups;
    return f.replace(/\{\{\s*\w+\s*\}\}/, out);
  }

  class PalettePicker extends HTMLElement {
    connectedCallback() {
      const utils = U();
      const holder = (this.parentElement && this.parentElement.querySelector('[data-palette-data]'))
        || this.querySelector('[data-palette-data]');
      if (!utils || !holder) return;

      let families = [];
      try { families = JSON.parse(holder.textContent); } catch (err) { return; }

      this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
      this.filterBase = this.dataset.filterBase || '';
      this.filterParam = this.dataset.filterParam || '';
      this.shades = this.buildShades(families, utils);
      if (!this.shades.length) return;

      this.els = {
        band: this.querySelector('[data-band]'),
        swatch: this.querySelector('[data-card-swatch]'),
        name: this.querySelector('[data-card-name]'),
        types: this.querySelector('[data-card-types]'),
        stock: this.querySelector('[data-card-stock]'),
        price: this.querySelector('[data-card-price]'),
        cta: this.querySelector('[data-card-cta]'),
        image: this.querySelector('[data-card-image]'),
        productLinks: Array.prototype.slice.call(this.querySelectorAll('[data-card-product-link]'))
      };
      if (!this.els.band) return;

      this.pinned = 0;
      this.renderBand();
      this.bind();
      this.show(0);
      this.weigh(-1);
      this.classList.add('is-ready');
    }

    /* O intrare de banda = o NUANTA, nu un produs: aceeasi culoare existenta la PLA Basic
       si la PETG HF apare o singura data, iar cardul de dedesubt le listeaza pe amandoua.
       Asa se poate raspunde la intrebarea „ce filamente am in culoarea asta". */
    buildShades(families, utils) {
      const map = utils.map();
      const by = {};
      families.forEach((f) => {
        const fam = map.by_family[f.h] || {};
        const famMulti = map.by_family_multi[f.h] || {};
        const mat = utils.bucket(f.m, f.t);

        /* Pentru o culoare care exista si ca Refill si cu bobina, pastram varianta din stoc
           si pretul cel mai mic — cardul arata „de la". */
        const variants = {};
        (f.v || []).forEach((row) => {
          const raw = String(row[0] || '');
          const cur = variants[raw];
          if (!cur || (!cur.available && row[2])) {
            variants[raw] = { id: row[1], available: !!row[2], price: row[3], image: row[4] || f.p || '' };
          }
        });

        Object.keys(variants).forEach((raw) => {
          /* Valorile optiunii poarta sufixe care nu tin de culoare: ambalaj „(Refill)",
             duritate „(TPU 90A - TPU 85A)", greutate „(0.5kg)". Tabelul de hex-uri le
             indexeaza fara sufix. */
          const key = raw.toLowerCase().replace(/\s*\([^)]*\)\s*$/, '').trim();
          if (!key) return;
          const label = raw.replace(/\s*\([^)]*\)\s*$/, '').trim() || raw;
          const multi = famMulti[key];
          const single = fam[key] || map.flat[key];
          const hexes = (multi && multi.length > 1) ? multi : (single ? [single] : null);
          if (!hexes) return;

          const v = variants[raw];
          const shade = by[key] || (by[key] = {
            key: key, name: label, hexes: hexes, sortHex: hexes[0], raws: [], items: []
          });
          if (shade.raws.indexOf(raw) === -1) shade.raws.push(raw);
          shade.items.push({
            family: f.t, handle: f.h, mat: mat,
            url: f.u + '?variant=' + v.id,
            available: v.available, price: v.price, image: v.image
          });
        });
      });

      const list = Object.keys(by).map((k) => by[k]);
      list.forEach((s) => {
        s.items.sort((a, b) => a.family.localeCompare(b.family));
        s.available = s.items.some((i) => i.available);
        const pool = s.items.filter((i) => i.available);
        const prices = (pool.length ? pool : s.items).map((i) => i.price)
          .filter((p) => typeof p === 'number');
        s.from = prices.length ? Math.min.apply(null, prices) : null;
      });
      list.sort(utils.spectrumSort);
      return list;
    }

    renderBand() {
      const utils = U();
      this.els.band.innerHTML = this.shades.map((s, i) =>
        '<button type="button" class="td-hp-sw" role="option" aria-selected="' + (i === 0) + '"' +
        ' tabindex="' + (i === 0 ? '0' : '-1') + '" data-i="' + i + '"' +
        ' style="background:' + utils.fill(s.hexes) + '"' +
        ' title="' + utils.esc(s.name) + '">' +
        '<span class="td-visually-hidden">' + utils.esc(s.name) + '</span></button>'
      ).join('');
      this.swatches = Array.prototype.slice.call(this.els.band.children);
    }

    /* Lupa: latimile se dau prin flex-grow, deci suma ramane constanta si banda nu se muta.
       `at < 0` inseamna „fara cursor" — toate nuantele revin la latimea egala.

       Surplusul nuantelor marite se scade din restul benzii JUMATATE din stanga si JUMATATE
       din dreapta. Nu e o alegere estetica, e conditia ca CENTRUL nuantei de sub cursor sa
       ramana pe loc: daca surplusul s-ar lua uniform (varianta evidenta, cu flex-grow 1 pe
       restul benzii), toate nuantele dinaintea celei active s-ar ingusta si marginea ei
       stanga ar aluneca — masurat la 156 de nuante, pana la 38px. Cursorul ajungea peste
       vecina, care se marea la randul ei: nuanta care creste nu mai era cea de sub mouse.

       Suma flex-grow ramane exact `n`, deci nuantele neatinse pastreaza latimea de baza. */
    weigh(at) {
      if (GRID_MODE.matches) return;
      const n = this.swatches.length;
      if (at < 0) {
        this.swatches.forEach((el) => { el.style.flexGrow = '1'; });
        return;
      }
      const extra = new Array(n).fill(0);
      let total = 0;
      for (let d = 0; d < WEIGHTS.length; d++) {
        const e = WEIGHTS[d] - 1;
        const targets = d === 0 ? [at] : [at - d, at + d];
        targets.forEach((i) => {
          if (i >= 0 && i < n) { extra[i] = e; total += e; }
        });
      }
      const left = [], right = [];
      for (let i = 0; i < n; i++) {
        if (!extra[i]) (i < at ? left : right).push(i);
      }
      /* Langa capetele benzii nu exista destule nuante pe o parte; ce nu incape acolo trece
         pe cealalta parte, iar cota per nuanta e plafonata ca sa nu ajunga la latime zero. */
      let needL = left.length ? total / 2 : 0;
      let needR = total - needL;
      if (!right.length) { needL = total; needR = 0; }
      const cap = 0.85;
      if (left.length && needL / left.length > cap) { needR += needL - left.length * cap; needL = left.length * cap; }
      if (right.length && needR / right.length > cap) { needL += needR - right.length * cap; needR = right.length * cap; }
      const perL = left.length ? Math.min(cap, needL / left.length) : 0;
      const perR = right.length ? Math.min(cap, needR / right.length) : 0;
      this.swatches.forEach((el, i) => {
        const g = extra[i] ? 1 + extra[i] : 1 - (i < at ? perL : perR);
        el.style.flexGrow = String(Math.max(0.05, g));
      });
    }

    show(i) {
      const s = this.shades[i];
      if (!s) return;
      const utils = U();
      this.els.swatch.style.background = utils.fill(s.hexes);
      this.els.name.textContent = s.name;
      this.els.types.innerHTML = s.items.map((it) =>
        '<a class="td-hp-type' + (it.available ? '' : ' is-out') + '" href="' + it.url + '">' +
        utils.esc(it.family) + '</a>'
      ).join('');
      this.els.stock.textContent = s.available
        ? (s.items.length === 1 ? 'În stoc' : 'În stoc · ' + s.items.length + ' filamente')
        : 'Momentan indisponibil';
      this.els.stock.classList.toggle('is-out', !s.available);
      this.els.price.textContent = s.from !== null ? 'de la ' + money(s.from, this.moneyFormat) : '';
      const imageItem = s.items.find((item) => item.available && item.image) || s.items.find((item) => item.image);
      const productItem = imageItem || s.items.find((item) => item.available) || s.items[0];
      if (this.els.image) {
        this.els.image.hidden = !imageItem;
        this.els.image.src = imageItem ? imageItem.image : '';
        this.els.image.alt = imageItem ? imageItem.family + ' în nuanța ' + s.name : '';
      }
      this.els.productLinks.forEach((link) => {
        link.href = productItem ? productItem.url : this.linkFor(s);
        link.setAttribute('aria-label', productItem
          ? 'Deschide ' + productItem.family + ' în nuanța ' + s.name
          : 'Deschide produsul în nuanța ' + s.name);
      });
      this.els.cta.href = productItem ? productItem.url : this.linkFor(s);
      this.els.cta.setAttribute('aria-label', productItem
        ? 'Vezi ' + productItem.family + ' în nuanța ' + s.name
        : 'Vezi produsul în nuanța ' + s.name);
    }

    /* Butonul duce la colectia reala, filtrata pe fateta de culoare a magazinului. Trimitem
       toate valorile brute ale nuantei (inclusiv „… (Refill)"), fiindca fateta Shopify
       filtreaza pe valoarea exacta a optiunii, iar repetarea parametrului inseamna SAU. */
    linkFor(s) {
      if (!this.filterBase || !this.filterParam) return s.items[0] ? s.items[0].url : '#';
      const q = s.raws.map((r) => this.filterParam + '=' + encodeURIComponent(r)).join('&');
      return this.filterBase + (this.filterBase.indexOf('?') === -1 ? '?' : '&') + q;
    }

    pin(i) {
      if (i === this.pinned) return;
      this.swatches[this.pinned].setAttribute('aria-selected', 'false');
      this.swatches[this.pinned].tabIndex = -1;
      this.pinned = i;
      this.swatches[i].setAttribute('aria-selected', 'true');
      this.swatches[i].tabIndex = 0;
      this.show(i);
    }

    indexAt(x, y) {
      const el = document.elementFromPoint(x, y);
      const sw = el && el.closest ? el.closest('.td-hp-sw') : null;
      return sw && this.els.band.contains(sw) ? Number(sw.dataset.i) : -1;
    }

    bind() {
      const band = this.els.band;

      band.addEventListener('pointerover', (e) => {
        const sw = e.target.closest('.td-hp-sw');
        if (!sw) return;
        const i = Number(sw.dataset.i);
        this.weigh(i);
        this.show(i);
      });

      /* La iesirea mouse-ului ramane afisata selectia fixata, nu ultima nuanta atinsa. */
      band.addEventListener('pointerleave', () => {
        if (this.dragging) return;
        this.weigh(-1);
        this.show(this.pinned);
      });

      band.addEventListener('click', (e) => {
        const sw = e.target.closest('.td-hp-sw');
        if (!sw) return;
        const index = Number(sw.dataset.i);
        this.pin(index);
        const shade = this.shades[index];
        const item = shade && (shade.items.find((entry) => entry.available) || shade.items[0]);
        if (item && !GRID_MODE.matches) window.location.href = item.url;
      });

      /* Atingere: tap si glisare orizontala fixeaza direct nuanta de sub deget. */
      band.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') return;
        this.dragging = true;
        const i = this.indexAt(e.clientX, e.clientY);
        if (i >= 0) { this.weigh(i); this.pin(i); }
      });
      band.addEventListener('pointermove', (e) => {
        if (!this.dragging) return;
        const i = this.indexAt(e.clientX, e.clientY);
        if (i >= 0) { this.weigh(i); this.pin(i); }
      });
      const stop = () => {
        if (!this.dragging) return;
        this.dragging = false;
        this.weigh(-1);
        this.show(this.pinned);
      };
      band.addEventListener('pointerup', stop);
      band.addEventListener('pointercancel', stop);

      /* Tastatura: sagetile plimba focusul (deci si preview-ul), Enter/Space fixeaza prin
         `click`-ul nativ al butonului. Roving tabindex — un singur swatch e in ordinea de
         tabulare, nu toate cele ~230. */
      band.addEventListener('keydown', (e) => {
        const from = e.target.closest('.td-hp-sw');
        if (!from) return;
        const cur = Number(from.dataset.i);
        let next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = Math.min(this.shades.length - 1, cur + 1);
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = Math.max(0, cur - 1);
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = this.shades.length - 1;
        else return;
        e.preventDefault();
        from.tabIndex = -1;
        this.swatches[next].tabIndex = 0;
        this.swatches[next].focus();
      });

      band.addEventListener('focusin', (e) => {
        const sw = e.target.closest('.td-hp-sw');
        if (!sw) return;
        const i = Number(sw.dataset.i);
        this.weigh(i);
        this.show(i);
      });
      band.addEventListener('focusout', (e) => {
        if (band.contains(e.relatedTarget)) return;
        this.weigh(-1);
        this.show(this.pinned);
      });
    }
  }

  if (!customElements.get('palette-picker')) {
    customElements.define('palette-picker', PalettePicker);
  }
})();
